import type { Step } from 'react-joyride'

export type RoleKey = 'ADMIN' | 'MANAGER' | 'DENTIST' | 'HYGIENIST' | 'RECEPTIONIST'
export type StepIcon =
  | 'sparkles'
  | 'search'
  | 'bell'
  | 'users'
  | 'calendar'
  | 'shield'
  | 'building'
  | 'credit-card'
  | 'bar-chart'
  | 'clipboard-list'
  | 'trending-up'
  | 'wallet'
  | 'megaphone'
  | 'package'
  | 'stethoscope'
  | 'file-check'
  | 'flask-conical'
  | 'bell-ring'
  | 'clipboard'
  | 'user-check'
  | 'receipt'
  | 'shield-check'

interface OnboardingStep extends Step {
  data?: {
    icon: StepIcon
  }
}

const welcome: OnboardingStep = {
  target: '[data-tour="dashboard"]',
  content: 'Este tour te guiara por las funciones principales de tu rol. Explora cada area haciendo click en los elementos resaltados.',
  placement: 'right',
  skipBeacon: true,
  title: 'Bienvenido a SmartDental',
  data: { icon: 'sparkles' },
}

const commandPalette: OnboardingStep = {
  target: '[data-tour="command-palette"]',
  content: 'Busca pacientes, ejecuta acciones rapidas o navega al instante. Tambien puedes abrirlo con Ctrl+K.',
  placement: 'bottom',
  title: 'Busqueda rapida',
  blockTargetInteraction: false,
  data: { icon: 'search' },
}

function nav(
  path: string,
  title: string,
  content: string,
  icon: StepIcon
): OnboardingStep {
  return {
    target: `[data-tour="nav-${path}"]`,
    content,
    placement: 'right',
    title,
    blockTargetInteraction: false,
    data: { icon },
  }
}

const ROLE_STEPS: Record<RoleKey, OnboardingStep[]> = {
  // 5 pasos: welcome + 3 nav + commandPalette
  ADMIN: [
    welcome,
    nav('patients', 'Pacientes', 'Accede a todas las fichas de pacientes de la clinica.', 'users'),
    nav('appointments', 'Calendario', 'Visualiza y gestiona la agenda de todos los profesionales.', 'calendar'),
    nav('billing', 'Facturacion', 'Revisa ingresos, facturas emitidas y estado de cobros.', 'credit-card'),
    commandPalette,
  ],

  MANAGER: [
    welcome,
    nav('appointments', 'Calendario', 'Supervisa la agenda de toda la clinica y la ocupacion de los gabinetes.', 'calendar'),
    nav('reports', 'Informes', 'Analiza el rendimiento con reportes de ingresos, ocupacion y tendencias.', 'bar-chart'),
    nav('billing', 'Facturacion', 'Vision financiera completa: ingresos, cobros pendientes y proyecciones.', 'wallet'),
    commandPalette,
  ],

  DENTIST: [
    welcome,
    nav('patients', 'Pacientes', 'Busca y accede a las fichas clinicas de tus pacientes.', 'users'),
    nav('appointments', 'Calendario', 'Tu agenda diaria. Visualiza, confirma y gestiona tus citas.', 'calendar'),
    nav('treatments', 'Tratamientos', 'Crea y gestiona planes de tratamiento para tus pacientes.', 'stethoscope'),
    commandPalette,
  ],

  HYGIENIST: [
    welcome,
    nav('patients', 'Pacientes', 'Accede al historial clinico y las notas de evolucion de tus pacientes.', 'users'),
    nav('appointments', 'Calendario', 'Tu agenda de higiene. Consulta las citas del dia y las proximas.', 'calendar'),
    nav('treatments', 'Tratamientos', 'Visualiza tratamientos activos y registra procedimientos de higiene.', 'stethoscope'),
    commandPalette,
  ],

  RECEPTIONIST: [
    welcome,
    nav('appointments', 'Calendario', 'Gestiona la agenda: programa, confirma y reagenda citas.', 'calendar'),
    nav('patients', 'Pacientes', 'Busca pacientes, actualiza datos de contacto y registra nuevos.', 'users'),
    nav('billing', 'Facturacion', 'Cobra consultas, emite facturas y gestiona pagos en mostrador.', 'credit-card'),
    commandPalette,
  ],
}

export function getStepsForRole(role: string): OnboardingStep[] {
  return ROLE_STEPS[role as RoleKey] || ROLE_STEPS.RECEPTIONIST
}
